import {
  LiquidGlassView as LiquidGlassViewFromLiquidGlass,
  isLiquidGlassSupported,
} from "@-/com.npmjs.callstack__liquid-glass";
import { Platform, View } from "@-/com.npmjs.react-native";
import { KeyboardAwareScrollView } from "@-/com.npmjs.react-native-keyboard-controller";
import Animated, {
  interpolateColor,
  runOnJS,
  type SharedValue,
  useAnimatedReaction,
  useAnimatedStyle,
  useReducedMotion,
} from "@-/com.npmjs.react-native-reanimated";
import { SafeAreaView, useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import { useCSSVariable, withUniwind } from "@-/com.npmjs.uniwind";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { FrostedSurface } from "@-/dev.improve.haniverse.components.ui.frosted-surface";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { AuthSheetContent, useAuthSheet } from "@-/dev.improve.haniverse.routes.nav.app.auth.sheet";
import { useEffect, useState } from "react";
import { WelcomeGalaxyHeadline } from "#welcome-galaxy-headline";
import { welcomeHaniWorldLayout } from "#welcome-hani-world-layout";
import type { WelcomeGalaxyAppearance } from "#welcome-galaxy-palette";
import {
  isWelcomeGalaxySplitLayout,
  lerp,
  smootherstep,
  WELCOME_GALAXY_BUTTON_FROST_START_T,
  WELCOME_GALAXY_INTRO_END_T,
  welcomeGalaxyButtonsOpacity,
  welcomeGalaxyEntryUiOpacity,
  welcomeGalaxyHeadlineHeight,
  welcomeGalaxyHeadlineProgress,
  welcomeGalaxyHeadlineWidth,
  type WelcomeGalaxyViewport,
} from "#welcome-galaxy-timeline";

const OverlaySafeAreaView = withUniwind(SafeAreaView);
const LiquidGlassView = withUniwind(LiquidGlassViewFromLiquidGlass);

/**
 * How visible the wordmark stays while the galaxy is held: faint enough for the galaxy to stand
 * out, without the name disappearing. The auth sheet keeps the wordmark at full opacity.
 */
const HEADLINE_FOCUSED_OPACITY = 0.3;

const TAGLINE = "Master Mandarin, Hanzi, and everything Chinese";
/** The tagline's advance in Han Composite Regular, in em. */
const TAGLINE_EM_WIDTH = 22.856;
/** The tagline spans just short of the actions beneath it. */
const TAGLINE_FILL = 0.96;

/** The margin around the portrait buttons (`m-2.25`). */
const BUTTONS_MARGIN = 9;
/** The landscape account panel's padding, and the widest its content grows. */
const PANEL_GUTTER = 24;
const PANEL_CONTENT_MAX_WIDTH = 400;

/** How far down Hani's artwork web's account card begins: just under the chin. */
const WEB_CARD_HANI_NECK = 0.5;
/** The least space above web's account card (`py-8`). */
const WEB_CARD_MIN_TOP = 32;

/**
 * The overlay's colours over each galaxy: white over deep space, and the splash's ink over the
 * light galaxy's paper. Get Started carries Haniverse orange and frosts over as the galaxy
 * settles; Log in uses regular tinted glass in both themes. Both are the height of the app's toolbar buttons (the menu and
 * the drawer): `useToolbarGridMetrics().diameter`.
 */
const WELCOME_GALAXY_OVERLAY_STYLES = {
  dark: {
    fallbackBackgrounds: ["rgba(255,255,255,0.08)", "rgba(255,255,255,0.32)"],
    fallbackClassName: "absolute inset-0 rounded-full border border-white/30",
    glassTints: { clear: "#FFFFFF12", frosted: "#FFFFFF60" },
    headlineColor: "white",
    panelClassName: "absolute inset-y-0 right-0 border-l border-white/10 bg-black/55",
    textClassName: "text-white",
  },
  light: {
    fallbackBackgrounds: ["rgba(10,10,10,0.06)", "rgba(10,10,10,0.12)"],
    fallbackClassName: "absolute inset-0 rounded-full border border-black/10",
    glassTints: { clear: "#0A0A0A0A", frosted: "#0A0A0A1C" },
    headlineColor: "#0a0a0a",
    panelClassName: "absolute inset-y-0 right-0 border-l border-black/10 bg-background/55",
    textClassName: "text-foreground",
  },
} as const satisfies Record<WelcomeGalaxyAppearance, unknown>;

/** The panel's sheet content stays put once the account step is done; the page navigates away. */
function keepAccountPanel() {}

/**
 * The tagline on one line, sized from its known advance to span `width` (the actions beneath it).
 * Native shrinks it should a font ever measure wider.
 */
function WelcomeGalaxyTagline({
  appearance,
  width,
}: {
  appearance: WelcomeGalaxyAppearance;
  width: number;
}) {
  const fontSize = (width * TAGLINE_FILL) / TAGLINE_EM_WIDTH;
  const { textClassName } = WELCOME_GALAXY_OVERLAY_STYLES[appearance];
  return (
    <Text
      adjustsFontSizeToFit={Platform.OS !== "web"}
      className={`text-center ${textClassName}`}
      numberOfLines={1}
      style={{ fontSize, lineHeight: Math.ceil(fontSize * 1.4) }}
    >
      {TAGLINE}
    </Text>
  );
}

/** One native material morph, triggered by the galaxy clock, without stacking glass surfaces. */
function WelcomeGalaxyButtonSurface({
  appearance,
  introTime,
  primary = false,
}: {
  appearance: WelcomeGalaxyAppearance;
  introTime: SharedValue<number>;
  primary?: boolean;
}) {
  const reduceMotion = useReducedMotion();
  const brandOrange = useCSSVariable("--color-website-accent");
  const orangeTint = typeof brandOrange === "string" ? brandOrange : "#ff7a3d";
  const { fallbackBackgrounds, fallbackClassName, glassTints } =
    WELCOME_GALAXY_OVERLAY_STYLES[appearance];
  const [settled, setSettled] = useState(
    () => primary && introTime.get() >= WELCOME_GALAXY_BUTTON_FROST_START_T,
  );
  useAnimatedReaction(
    () => primary && introTime.get() >= WELCOME_GALAXY_BUTTON_FROST_START_T,
    (next, previous) => {
      if (next !== previous) {
        runOnJS(setSettled)(next);
      }
    },
    [primary],
  );
  const fallbackStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      primary
        ? smootherstep(
            WELCOME_GALAXY_BUTTON_FROST_START_T,
            WELCOME_GALAXY_INTRO_END_T,
            introTime.get(),
          )
        : 1,
      [0, 1],
      primary ? [`${orangeTint}38`, `${orangeTint}80`] : fallbackBackgrounds,
    ),
  }));

  if (Platform.OS === "android") {
    return (
      <FrostedSurface className="absolute inset-0 rounded-full" intensity={80} pointerEvents="none">
        {primary ? (
          <View className="absolute inset-0 bg-website-accent/40" pointerEvents="none" />
        ) : null}
      </FrostedSurface>
    );
  }
  if (!isLiquidGlassSupported) {
    return (
      <Animated.View className={fallbackClassName} pointerEvents="none" style={fallbackStyle} />
    );
  }

  const frosted = primary && settled;
  return (
    <LiquidGlassView
      animated={!reduceMotion}
      animationDuration={(WELCOME_GALAXY_INTRO_END_T - WELCOME_GALAXY_BUTTON_FROST_START_T) * 1000}
      className="absolute inset-0 rounded-full"
      colorScheme={appearance}
      effect={primary && !frosted ? "clear" : "regular"}
      interactive={false}
      pointerEvents="none"
      tintColor={primary ? `${orangeTint}${frosted ? "80" : "50"}` : glassTints.frosted}
    />
  );
}

/** Portrait: the tagline over Get Started and Log in, which open the auth sheet. */
function WelcomeGalaxyButtons({
  appearance,
  introTime,
  viewport,
}: {
  appearance: WelcomeGalaxyAppearance;
  introTime: SharedValue<number>;
  viewport: WelcomeGalaxyViewport;
}) {
  const { openAccount, openGetStarted } = useAuthSheet();
  const { diameter } = useToolbarGridMetrics();
  const { textClassName } = WELCOME_GALAXY_OVERLAY_STYLES[appearance];
  return (
    <OverlaySafeAreaView
      className="absolute inset-0"
      edges={["top", "bottom"]}
      pointerEvents="box-none"
    >
      <View className="flex-1" pointerEvents="none" />
      <View className="m-2.25 shrink-0 gap-2.25">
        <WelcomeGalaxyTagline appearance={appearance} width={viewport.width - 2 * BUTTONS_MARGIN} />
        <Button
          accessibilityLabel="Get Started"
          className="relative rounded-full bg-transparent px-2 active:opacity-80"
          onPress={() => openGetStarted({ source: "welcome" })}
          size="fill"
          style={{ height: diameter }}
          testID="auth-get-started-button"
          variant="link"
        >
          <WelcomeGalaxyButtonSurface appearance={appearance} introTime={introTime} primary />
          <Text className={textClassName}>Get Started</Text>
        </Button>
        <Button
          accessibilityLabel="Log in"
          className="relative rounded-full bg-transparent px-2 active:opacity-80"
          onPress={() => openAccount({ mode: "signIn", source: "welcome" })}
          size="fill"
          style={{ height: diameter }}
          testID="auth-sign-in-button"
          variant="link"
        >
          <WelcomeGalaxyButtonSurface appearance={appearance} introTime={introTime} />
          <Text className={textClassName}>Log in</Text>
        </Button>
      </View>
    </OverlaySafeAreaView>
  );
}

/** Web starts with the complete sign-in form and switches account modes inside one surface. */
function WelcomeGalaxyWebCard({
  appearance,
  viewport,
}: {
  appearance: WelcomeGalaxyAppearance;
  viewport: WelcomeGalaxyViewport;
}) {
  const { close, welcomeRequest } = useAuthSheet();
  const insets = useSafeAreaInsets();
  const { diameter } = useToolbarGridMetrics();
  // The same seat `WelcomeHaniWorld` draws Hani at on web.
  const { haniSize, haniTop } = welcomeHaniWorldLayout({
    bottomInset: insets.bottom,
    buttonDiameter: diameter,
    isWeb: true,
    viewport,
  });
  return (
    <KeyboardAwareScrollView
      className="flex-1"
      contentContainerClassName="grow items-center px-5 pb-8"
      // The card begins at Hani's neck: the head rises over its top edge, the body stays behind it.
      contentContainerStyle={{
        paddingTop: Math.max(WEB_CARD_MIN_TOP, haniTop + haniSize * WEB_CARD_HANI_NECK),
      }}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
    >
      {/* Opaque, so Hani's body and the globe stay behind the card while the head peeks over. */}
      <View
        className="w-full max-w-md gap-5 overflow-hidden rounded-xs border border-border bg-background p-8 shadow-xl"
        testID="auth-welcome-card"
      >
        <View className="items-center">
          <WelcomeGalaxyHeadline
            color={WELCOME_GALAXY_OVERLAY_STYLES[appearance].headlineColor}
            height={32}
            width={Math.max(0, Math.min(124, viewport.width - 108))}
          />
        </View>
        <AuthSheetContent
          close={close}
          email={welcomeRequest?.email ?? ""}
          initialMode={welcomeRequest?.mode ?? "signIn"}
          key={welcomeRequest?.openCount ?? 0}
          presentation="card"
          signUpKind="account"
        />
      </View>
    </KeyboardAwareScrollView>
  );
}

/**
 * Landscape: the right half is the auth sheet's content itself (the Log In / Sign Up toggle over
 * Apple, Google and email), centred under the tagline, so nothing needs to open over the galaxy.
 */
function WelcomeGalaxyAccountPanel({
  appearance,
  viewport,
}: {
  appearance: WelcomeGalaxyAppearance;
  viewport: WelcomeGalaxyViewport;
}) {
  const insets = useSafeAreaInsets();
  const panelWidth = viewport.width - (viewport.stageWidth ?? viewport.width);
  const contentWidth = Math.max(
    0,
    Math.min(PANEL_CONTENT_MAX_WIDTH, panelWidth - insets.right - 2 * PANEL_GUTTER),
  );
  return (
    <View
      className={WELCOME_GALAXY_OVERLAY_STYLES[appearance].panelClassName}
      style={{ width: panelWidth }}
      testID="auth-welcome-account-panel"
    >
      <KeyboardAwareScrollView
        bottomOffset={PANEL_GUTTER}
        contentContainerClassName="grow items-center justify-center"
        contentContainerStyle={{
          paddingBottom: insets.bottom + PANEL_GUTTER,
          paddingRight: insets.right,
          paddingTop: insets.top + PANEL_GUTTER,
        }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View className="gap-5" style={{ width: contentWidth }}>
          <WelcomeGalaxyTagline appearance={appearance} width={contentWidth} />
          <AuthSheetContent
            close={keepAccountPanel}
            email=""
            initialMode="signUp"
            signUpKind="getStarted"
          />
        </View>
      </KeyboardAwareScrollView>
    </View>
  );
}

/**
 * Everything drawn with the galaxy, driven by the same clock: the Haniverse wordmark and
 * account actions. In portrait those are the tagline
 * over Get Started and Log in; in landscape the galaxy takes the left half and the auth sheet's
 * content fills the right. The actions start faint but are fully tappable from the first frame, so
 * a returning user can skip the show. Everything fades out as the entry into the Haniverse begins
 * (`entryTime`).
 */
export function WelcomeGalaxyOverlay({
  appearance,
  entryTime,
  focusProgress,
  introTime,
  viewport,
}: {
  appearance: WelcomeGalaxyAppearance;
  entryTime: SharedValue<number>;
  /** 1 while the galaxy is held, which fades the wordmark (sheet open does not fade it). */
  focusProgress: SharedValue<number>;
  /** The clock, held once the intro is over (see `useWelcomeGalaxyClock`). */
  introTime: SharedValue<number>;
  viewport: WelcomeGalaxyViewport;
}) {
  const { close } = useAuthSheet();
  const insets = useSafeAreaInsets();
  const isSplit = Platform.OS !== "web" && isWelcomeGalaxySplitLayout(viewport);
  // Until the page has a size (web, before it measures itself), it has no layout to place actions in.
  const hasSize = viewport.width > 0 && viewport.height > 0;
  // Landscape keeps the wordmark clear of the notch beside it.
  const sideInset = isSplit ? Math.max(insets.left, insets.right) : 0;
  const headlineWidth = welcomeGalaxyHeadlineWidth(viewport, sideInset);
  const { diameter } = useToolbarGridMetrics();
  const worldLayout = welcomeHaniWorldLayout({
    bottomInset: insets.bottom,
    buttonDiameter: diameter,
    sideInset,
    viewport,
  });

  // The landscape panel replaces the sheet, so a sheet opened in portrait gives way to it.
  useEffect(() => {
    if (isSplit) {
      close();
    }
  }, [close, isSplit]);

  const headlineStyle = useAnimatedStyle(() => {
    const progress = welcomeGalaxyHeadlineProgress(introTime.get());
    return {
      opacity:
        progress *
        welcomeGalaxyEntryUiOpacity(entryTime.get()) *
        lerp(1, HEADLINE_FOCUSED_OPACITY, focusProgress.get()),
      transform: [{ translateY: lerp(12, 0, progress) }],
    };
  });
  const actionsStyle = useAnimatedStyle(() => ({
    opacity:
      (Platform.OS === "web" ? 1 : welcomeGalaxyButtonsOpacity(introTime.get())) *
      welcomeGalaxyEntryUiOpacity(entryTime.get()),
  }));

  return (
    <>
      {Platform.OS === "web" ? null : (
        <View
          className="absolute"
          pointerEvents="none"
          style={{
            left: ((viewport.stageWidth ?? viewport.width) - headlineWidth) / 2,
            top: worldLayout.headlineTop,
            width: headlineWidth,
          }}
        >
          <Animated.View style={headlineStyle}>
            <WelcomeGalaxyHeadline
              color={WELCOME_GALAXY_OVERLAY_STYLES[appearance].headlineColor}
              height={welcomeGalaxyHeadlineHeight(viewport, sideInset)}
              width={headlineWidth}
            />
          </Animated.View>
        </View>
      )}
      <Animated.View className="absolute inset-0" pointerEvents="box-none" style={actionsStyle}>
        {!hasSize ? null : Platform.OS === "web" ? (
          <WelcomeGalaxyWebCard appearance={appearance} viewport={viewport} />
        ) : isSplit ? (
          <WelcomeGalaxyAccountPanel appearance={appearance} viewport={viewport} />
        ) : (
          <WelcomeGalaxyButtons appearance={appearance} introTime={introTime} viewport={viewport} />
        )}
      </Animated.View>
    </>
  );
}
